import { Component, ErrorInfo, ReactNode } from "react";
import { Button } from "@/components/ui/button";
import { ErrorState } from "./error-state";

interface Props {
  children: ReactNode;
  // When this changes (e.g. the current route) a caught error is cleared, so
  // navigating away recovers instead of the whole app staying on the fallback
  // until a full reload.
  resetKey?: unknown;
}

interface State {
  hasError: boolean;
  resetKey: unknown;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, resetKey: this.props.resetKey };

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State,
  ): Partial<State> | null {
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey };
    }
    return null;
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Uncaught error:", error, info);
  }

  render() {
    if (this.state.hasError) {
      return (
        <ErrorState
          title="Something went wrong"
          description="An unexpected error occurred. Try reloading the page."
          action={
            <Button onClick={() => window.location.reload()}>Reload</Button>
          }
          fullScreen
        />
      );
    }

    return this.props.children;
  }
}
